import { inject, Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { ECampaignStatus } from '@enums/campaign.enum';
import {
  ICampaignAnalyticsApi,
  ICampaignAnalyticsApiResponse,
  ICtaKpiStatusApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbComponent, IBreadcrumb } from '@shared/components/breadcrumb/breadcrumb.component';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { isWithinInterval } from 'date-fns';
import { TCampaignApi } from '../campaigns.interface';
import {
  CAMPAIGN_DETAILS_ALL_TABS,
  ECampaignDetailsTab,
  ICampaignAnalyticsFilter,
  ICampaignDetailsQueryState,
} from './campaign-details.interface';
import type { ICampaignDetailsAnalyticsFetchParams, ICampaignDetailsState } from './campaign-details.store';

@Injectable({
  providedIn: 'root',
})
export class CampaignDetailsService {
  private readonly translate = inject(TranslateService);
  private readonly campaignAnalyticsUtilitiesService = inject(CampaignAnalyticsUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);

  public getBreadcrumbs(
    adminMode: boolean,
    creatorMode: boolean,
    creatorName: string,
    exportMode: boolean,
  ): IBreadcrumb[] {
    const firstBreadcrumb = this.getFirstBreadcrumb(adminMode, creatorMode, creatorName);

    if (exportMode) {
      return [];
    }

    return [firstBreadcrumb, { label: BreadcrumbComponent.loadingLabel }];
  }

  public getCampaignDetailsTableData(
    campaign: TCampaignApi,
    campaignAnalytics: ICampaignAnalyticsApi | TNil,
  ): [ICampaignAnalytics, IAnalyticsNode[]] {
    return this.campaignAnalyticsUtilitiesService.getCampaignAnalyticsData(campaign, campaignAnalytics);
  }

  public createEmptyCtaKpiStatusApi(): ICtaKpiStatusApi {
    return {
      currentBonusContents: 0,
      currentBonusDeliverables: 0,
      currentFailedContents: 0,
      currentFailedDeliverables: 0,
      currentFailedKpis: 0,
      currentMatchedContents: 0,
      currentMissingContents: 0,
      currentOkContents: 0,
      currentOkDeliverables: 0,
      currentOkKpis: 0,
      currentProblemContents: 0,
      currentProblemDeliverables: 0,
      currentProblemKpis: 0,
      currentProcessingContents: 0,
      currentProcessingDeliverables: 0,
      currentProcessingKpis: 0,
      currentUnrelatedContents: 0,
      expectedContents: 0,
      expectedDeliverables: 0,
      expectedKpis: 0,
      totalDeliverables: 0,
    };
  }

  public getFilterSignature(filter: ICampaignAnalyticsFilter): string {
    return JSON.stringify({
      creatorIds: Array.from(filter.creatorIds).sort(),
      endDate: filter.endDate?.toISOString() ?? null,
      platformFeatures: Array.from(filter.platformFeatures).sort(),
      startDate: filter.startDate?.toISOString() ?? null,
    });
  }

  public isCurrentQueryStateRequest<T>(
    state: ICampaignDetailsQueryState<T>,
    requestKey: number,
    filterSignature: string,
  ): boolean {
    return state.requestKey === requestKey && state.filterSignature === filterSignature;
  }

  public getEffectiveAnalyticsCampaignId(
    campaignDetailsState: ICampaignDetailsState,
    params: Pick<ICampaignDetailsAnalyticsFetchParams, 'campaignId' | 'creatorBriefingMode' | 'exportMode'> | null,
  ): string | null {
    if (!params) {
      return null;
    }

    if (params.exportMode) {
      return campaignDetailsState.campaignExport?.campaignId ?? null;
    }

    if (params.creatorBriefingMode) {
      return campaignDetailsState.campaign?.id ?? null;
    }

    return params.campaignId;
  }

  public isVersionChanged(state: ICampaignDetailsState, response: ICampaignAnalyticsApiResponse): boolean {
    return (
      state.campaignAnalyticsMap[response.campaignAnalytics.id]?.version !== response.campaignAnalytics.version &&
      state.campaignAnalyticsMap[response.campaignAnalytics.id]?.lastChanged !== response.campaignAnalytics.lastChanged
    );
  }

  public isCampaignLive(campaign: TCampaignApi): boolean {
    if (!campaign || !campaign.originalStart || !campaign.originalEnd) {
      return false;
    }

    const now = new Date();

    return (
      [ECampaignStatus.planned, ECampaignStatus.live].includes(campaign.status) ||
      isWithinInterval(now, {
        end: this.dateUtils.parseDateTimeFromApi(campaign.originalEnd),
        start: this.dateUtils.parseDateTimeFromApi(campaign.originalStart),
      })
    );
  }

  public getVisibleTabs(
    params: ICampaignDetailsAnalyticsFetchParams,
    enabledTabs?: Set<ECampaignDetailsTab>,
  ): ECampaignDetailsTab[] {
    if (params.creatorBriefingMode) {
      return [
        ECampaignDetailsTab.overview,
        ECampaignDetailsTab.deliverables,
        ECampaignDetailsTab.contents,
        ECampaignDetailsTab.creators,
      ];
    }

    const allTabs = CAMPAIGN_DETAILS_ALL_TABS;

    if (!enabledTabs) {
      return allTabs;
    }

    return allTabs.filter((tab) => enabledTabs.has(tab));
  }

  private getFirstBreadcrumb(adminMode: boolean, creatorMode: boolean, creatorName: string): IBreadcrumb {
    if (adminMode) {
      return {
        label: this.translate.instant('pages.admin.adminCampaigns.banner.title'),
        link: '/admin/campaigns',
      };
    }

    if (creatorMode) {
      return {
        label: creatorName,
        link: '/creator',
      };
    }

    return {
      label: this.translate.instant('pages.campaigns.banner.title'),
      link: '/campaigns',
    };
  }
}
